import { Box, Flex, HStack, Stack, Text, chakra } from '@chakra-ui/react';
import { Activity, AlertTriangle, Loader2, Waves } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { getMarketCatalystEvents, getRecentCatalystEvents } from '../api/client';
import type { CatalystEvent, Quake } from '../api/types';
import { pctFromProb, pp, spreadPct } from '../lib/format';
import { CatalystEventsList } from './CatalystEventsList';
import { eventDisplayTitle, marketDisplayLabel } from './eventThesis';

// The right panel is named after its source, not after an interpretation.
// Three kinds of signal live here, and only one of them is a real
// catalyst: external news. The other two — related-market movement
// and timing-boundary logic — are evidence of internal consistency,
// not causes. Calling all three "Likely Catalysts" was overclaiming.
export function LikelyCatalysts({
  leader,
  height,
  onViewAllSignals,
}: {
  leader: Quake | undefined;
  height: number;
  onViewAllSignals: () => void;
}) {
  const marketID = leader?.market_id ?? '';
  const hasLeader = marketID.length > 0;
  const recentQuery = useQuery({
    queryKey: ['catalysts', 'recent'],
    queryFn: ({ signal }) => getRecentCatalystEvents(20, { signal }),
    enabled: !hasLeader,
    refetchInterval: 10_000,
  });
  const marketQuery = useQuery({
    queryKey: ['catalysts', 'market', marketID],
    queryFn: ({ signal }) => getMarketCatalystEvents(marketID, 10, { signal }),
    enabled: hasLeader,
    refetchInterval: 10_000,
  });

  const previewCount = Math.max(1, Math.min(5, Math.floor((height - 104) / 154)));
  const items = mergeCatalysts(marketQuery.data?.items ?? [], hasLeader ? [] : (recentQuery.data?.items ?? []));
  const visibleItems = items.slice(0, previewCount);
  const isUnavailable = items.length === 0 && (hasLeader ? marketQuery.isError : recentQuery.isError);
  const isFetching = recentQuery.isFetching || marketQuery.isFetching;

  // The subtitle is a list of the three signal sources, not a
  // marketing claim. "Catalyst" is reserved for external news.
  const hasExternalNews = items.some((item) => item.source === 'external_news');
  const hasRelated = items.some((item) => item.source === 'related_market_cluster');
  const hasTimingOrLiquidity = items.some(
    (item) => item.source === 'timing_boundary' || item.source === 'liquidity_warning',
  );
  const subtitleParts: string[] = [];
  if (hasExternalNews) subtitleParts.push('news');
  if (hasRelated) subtitleParts.push('related movement');
  if (hasTimingOrLiquidity) subtitleParts.push('timing/liquidity');
  const subtitle =
    subtitleParts.length > 0 ? subtitleParts.join(' + ') : hasLeader ? 'selected event summary' : 'no context matched';

  return (
    <Box as="section" layerStyle="glassPanel" borderRadius="oqPanel" display="flex" flexDirection="column" overflow="hidden" minH="520px" style={{ height }}>
      <Flex
        flexShrink={0}
        align="center"
        justify="space-between"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        px={5}
        py={3}
      >
        <HStack gap={3}>
          <Box display="grid" placeItems="center" h={6} w={6} borderRadius="oqControl" bg="purple.600" fontSize="sm" fontWeight="bold" color="purple.50">
            2
          </Box>
          <Stack gap={0}>
            <Text fontSize="lg" fontWeight="semibold" color="text.primary">
              Market Context
            </Text>
            <Text fontSize="xs" color="text.muted">
              Related movement + possible catalysts. Only "news" is a true external catalyst.
            </Text>
          </Stack>
        </HStack>
        {isFetching ? (
          <Loader2 className="oq-spin" size={16} color="currentColor" />
        ) : (
          <Text fontSize="xs" color="text.muted">
            {subtitle}
          </Text>
        )}
      </Flex>

      <Box minH={0} flex="1" overflow="hidden">
        {isUnavailable ? (
          <CatalystEmptyState title="Signals unavailable." detail="The live movers feed is still working." />
        ) : visibleItems.length === 0 ? (
          hasLeader && leader ? (
            <SelectedEventExplanation leader={leader} />
          ) : (
            <CatalystEmptyState
              title="No context yet."
              detail="Watching related-market clusters, timing boundaries, liquidity warnings, and external news matches."
            />
          )
        ) : (
          <CatalystEventsList items={visibleItems} leaderMarketID={marketID} mode="compact" />
        )}
      </Box>

      {items.length > visibleItems.length ? (
        <chakra.button
          type="button"
          onClick={onViewAllSignals}
          display="flex"
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
          borderTopWidth="1px"
          borderColor="border.subtle"
          px={5}
          py={3}
          fontSize="sm"
          fontWeight="semibold"
          color="text.muted"
          transition="colors"
          _hover={{ color: 'text.primary' }}
        >
          View all signals
        </chakra.button>
      ) : null}
    </Box>
  );
}

function SelectedEventExplanation({ leader }: { leader: Quake }) {
  const eventTitle = eventDisplayTitle(leader.event_slug, [leader]);
  const marketLabel = marketDisplayLabel(leader.question, eventTitle);
  const positive = leader.change_pp >= 0;
  const toward = positive ? 'toward' : 'away from';
  const liquidityRisk = leader.signal.toLowerCase().includes('liquidity');

  return (
    <Box h="full" overflowY="auto" px={5} py={5}>
      <Stack borderRadius="oqControl" borderWidth="1px" borderColor="border.subtle" bg="surface.control" p={4} gap={4}>
        <Flex align="flex-start" gap={3}>
          <Flex
            display="grid"
            placeItems="center"
            h={10}
            w={10}
            flexShrink={0}
            borderRadius="oqControl"
            borderWidth="1px"
            borderColor="brand.solid/20"
            bg="brand.subtle"
            color="brand.fg"
          >
            <Activity size={20} />
          </Flex>
          <Box minW={0}>
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="text.muted">
              Selected event
            </Text>
            <Text mt={1} fontSize="xl" fontWeight="semibold" lineHeight="tight" color="text.primary" lineClamp={2}>
              {eventTitle}
            </Text>
            <Text mt={2} fontSize="sm" lineHeight="6" color="text.muted">
              {marketLabel} moved {toward} {leader.outcome || 'the selected outcome'}.
            </Text>
          </Box>
        </Flex>

        <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2}>
          <ContextStat label="Markets" value="1 confirmed" />
          <ContextStat
            label="Price"
            value={`${pctFromProb(leader.old_price)} -> ${pctFromProb(leader.new_price)}`}
          />
          <ContextStat label="Move" value={pp(leader.change_pp)} tone={positive ? 'good' : 'bad'} />
          <ContextStat label="Spike" value={ratio(leader.volume_spike_ratio)} />
          <ContextStat label="Persist" value={ratio(leader.directional_persistence)} tone="good" />
          <ContextStat label="Score" value={leader.score.toFixed(0)} />
        </Box>
      </Stack>

      <Stack mt={3} borderRadius="oqControl" borderWidth="1px" borderColor="border.subtle" bg="surface.control" p={4} gap={3}>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="text.muted">
          Context status
        </Text>
        <Stack as="ul" listStyleType="none" gap={2} fontSize="sm" lineHeight="6" color="text.primary">
          <ContextStatusLine text="No related-market confirmation matched yet." />
          <ContextStatusLine text="No external catalyst matched." />
          <ContextStatusLine
            text={
              liquidityRisk
                ? `Liquidity warning present: spread ${spreadPct(leader.spread)}.`
                : 'Liquidity warning: none.'
            }
            warn={liquidityRisk}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

function ContextStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <Stack borderRadius="oqControl" borderWidth="1px" borderColor="border.subtle" bg="whiteAlpha.50" p={3} gap={0}>
      <Text fontSize="11px" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="text.muted">
        {label}
      </Text>
      <Text
        mt={1}
        fontWeight="semibold"
        color={tone === 'good' ? 'signal.good' : tone === 'bad' ? 'signal.bad' : 'text.primary'}
      >
        {value}
      </Text>
    </Stack>
  );
}

function ContextStatusLine({ text, warn = false }: { text: string; warn?: boolean }) {
  return (
    <Stack as="li" direction="row" align="flex-start" gap={2}>
      <Box
        as="span"
        mt={2}
        h="1.5"
        w="1.5"
        flexShrink={0}
        borderRadius="full"
        bg={warn ? 'signal.warn' : 'text.muted'}
        opacity={warn ? 1 : 0.7}
        boxShadow={warn ? '0 0 12px var(--oq-colors-signal-warn)' : undefined}
      />
      <Text as="span" color={warn ? 'signal.warn' : 'text.muted'}>
        {text}
      </Text>
      {warn ? (
        <Box as="span" mt={0.5} w="4" h="4" flexShrink={0} color="signal.warn">
          <AlertTriangle />
        </Box>
      ) : null}
    </Stack>
  );
}

function CatalystEmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <Box display="grid" minH="full" placeItems="center" px={5} py={8}>
      <Stack maxW="sm" align="center" textAlign="center" gap={0}>
        <Flex
          display="grid"
          placeItems="center"
          h={12}
          w={12}
          mx="auto"
          borderRadius="oqPanel"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="blackAlpha.200"
          color="purple.400"
        >
          <Waves size={24} />
        </Flex>
        <Text mt={5} fontSize="lg" fontWeight="semibold" color="text.primary">
          {title}
        </Text>
        <Text mt={2} fontSize="sm" lineHeight="6" color="text.muted">
          {detail}
        </Text>
        <Stack mt={6} borderRadius="oqPanel" borderWidth="1px" borderColor="border.subtle" bg="surface.control" p={4} textAlign="left" gap={0}>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="text.muted">
            Source map
          </Text>
          <Stack as="ul" listStyleType="none" mt={2} gap={1} fontSize="sm" color="text.primary">
            <li>Related movement — markets moving together</li>
            <li>Timing boundary — end-of-window price jumps</li>
            <li>Liquidity warning — wide spread / thin book</li>
            <Text as="li" fontWeight="semibold" color="signal.good">
              External news — only true catalyst
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

function mergeCatalysts(primary: CatalystEvent[], recent: CatalystEvent[]) {
  const seen = new Set<string>();
  const out: CatalystEvent[] = [];
  for (const item of [...primary, ...recent]) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  // Source ranking is a real product decision, not a tiebreaker. The
  // hierarchy is: external news > related movement > timing > liquidity.
  // External news is the only signal that actually explains a price
  // move. Related movement is corroboration. Timing/liquidity are
  // noisiness flags. We sort by rank so the user reads the strong
  // signal first, not the loudest badge.
  out.sort((a, b) => sourceRank(a.source) - sourceRank(b.source));
  return out;
}

function sourceRank(source: string): number {
  if (source === 'external_news') return 0;
  if (source === 'related_market_cluster') return 1;
  if (source === 'timing_boundary') return 2;
  if (source === 'liquidity_warning') return 3;
  return 4;
}

function ratio(value: number) {
  if (!Number.isFinite(value)) return '-';
  return `${value.toFixed(value >= 10 ? 0 : 2)}x`;
}